import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Sparkles, Save, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

interface BrandProfile {
  brand_name: string;
  brand_voice: string;
  website: string;
  support_email: string;
  support_phone: string;
  whatsapp: string;
  signature: string;
  default_greeting: string;
  boilerplate_lines: string[];
  updated_at?: string | null;
  updated_by?: string | null;
}

const VOICE_OPTIONS: { id: string; label: string; hint: string }[] = [
  { id: "friendly", label: "Friendly", hint: "Warm, upbeat, uses emoji." },
  { id: "professional", label: "Professional", hint: "Formal, precise, brand-safe." },
  { id: "witty", label: "Witty", hint: "Playful, clever, small brand-of-one." },
  { id: "empathetic", label: "Empathetic", hint: "Soft, caring, complaint-heavy audiences." },
  { id: "concise", label: "Concise", hint: "One-liner replies. No fluff." },
];

export const BrandProfileCard: React.FC = () => {
  const qc = useQueryClient();
  const q = useQuery<BrandProfile>({
    queryKey: ["brand-profile"],
    queryFn: async () => (await api.get("/settings/brand-profile")).data,
  });

  const [brand, setBrand] = React.useState<BrandProfile>({
    brand_name: "", brand_voice: "friendly", website: "", support_email: "",
    support_phone: "", whatsapp: "", signature: "", default_greeting: "",
    boilerplate_lines: [],
  });
  const [newBoiler, setNewBoiler] = React.useState("");
  const seeded = React.useRef(false);
  React.useEffect(() => {
    if (seeded.current || !q.data) return;
    seeded.current = true;
    setBrand({ ...q.data, boilerplate_lines: q.data.boilerplate_lines || [] });
  }, [q.data]);

  const save = useMutation({
    mutationFn: async () => (await api.put("/settings/brand-profile", brand)).data as BrandProfile,
    onSuccess: (d) => {
      toast.success("Brand profile saved");
      qc.invalidateQueries({ queryKey: ["brand-profile"] });
      setBrand({ ...d, boilerplate_lines: d.boilerplate_lines || [] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const set = <K extends keyof BrandProfile>(k: K, v: BrandProfile[K]) =>
    setBrand((b) => ({ ...b, [k]: v }));

  return (
    <div className="sg-card p-6 mt-6" data-testid="brand-profile-card">
      <div className="flex items-start gap-3 mb-5">
        <div className="h-10 w-10 rounded-xl bg-violet-100 flex items-center justify-center shrink-0">
          <Sparkles className="h-5 w-5 text-violet-700" />
        </div>
        <div>
          <div className="font-display text-lg font-semibold tracking-tight">
            Brand profile
          </div>
          <p className="text-sm text-[color:var(--sg-muted)] mt-1 max-w-xl">
            Used by the AI reply engine and bulk-reply flows to sound like a
            real human at your brand. The more you fill in here, the more
            personal each reply feels — website URL, support channels, brand
            voice, and any boilerplate lines the AI should reuse verbatim.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Brand name</Label>
          <Input data-testid="brand-name" value={brand.brand_name} onChange={(e) => set("brand_name", e.target.value)} className="mt-1.5 bg-white" placeholder="e.g. SociBaba" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Website</Label>
          <Input data-testid="brand-website" value={brand.website} onChange={(e) => set("website", e.target.value)} className="mt-1.5 bg-white" placeholder="https://www.example.com" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Support email</Label>
          <Input data-testid="brand-support-email" value={brand.support_email} onChange={(e) => set("support_email", e.target.value)} className="mt-1.5 bg-white" placeholder="support@example.com" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Support phone</Label>
          <Input data-testid="brand-support-phone" value={brand.support_phone} onChange={(e) => set("support_phone", e.target.value)} className="mt-1.5 bg-white" placeholder="+91 9739959565" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">WhatsApp</Label>
          <Input data-testid="brand-whatsapp" value={brand.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className="mt-1.5 bg-white" placeholder="wa.me/919739959565" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Signature (optional)</Label>
          <Input data-testid="brand-signature" value={brand.signature} onChange={(e) => set("signature", e.target.value)} className="mt-1.5 bg-white" placeholder="Leave blank — reply is already signed by your channel" />
          <div className="text-[11px] text-[color:var(--sg-muted)] mt-1">
            Replies are automatically posted <b>by your connected channel</b> — the platform shows the channel name next to every reply, so a text signature is usually redundant. Only fill this in if you want a fixed sign-off line.
          </div>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Default greeting</Label>
          <Input data-testid="brand-greeting" value={brand.default_greeting} onChange={(e) => set("default_greeting", e.target.value)} className="mt-1.5 bg-white" placeholder="Hey {name}! Thanks so much for reaching out." />
          <div className="text-[11px] text-[color:var(--sg-muted)] mt-1">Optional. Use <span className="font-mono">{"{name}"}</span> as a placeholder for the commenter's handle.</div>
        </div>
      </div>

      {/* Voice picker */}
      <div className="mt-5">
        <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Brand voice</Label>
        <div className="grid sm:grid-cols-5 gap-2 mt-2">
          {VOICE_OPTIONS.map((v) => (
            <button
              type="button"
              key={v.id}
              data-testid={`brand-voice-${v.id}`}
              onClick={() => set("brand_voice", v.id)}
              className={`text-left border rounded-lg p-3 transition-all ${
                brand.brand_voice === v.id
                  ? "border-[color:var(--sg-violet)] bg-violet-50 ring-2 ring-[color:var(--sg-violet)]/20"
                  : "border-[color:var(--sg-border)] hover:border-[color:var(--sg-fg)]"
              }`}
            >
              <div className="text-sm font-semibold">{v.label}</div>
              <div className="text-[11px] text-[color:var(--sg-muted)] mt-0.5 leading-tight">{v.hint}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Boilerplate */}
      <div className="mt-5">
        <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Approved boilerplate lines</Label>
        <div className="text-[11px] text-[color:var(--sg-muted)] mt-0.5 mb-2">
          The AI will re-use these verbatim when the intent matches. Example: <i>"Order status? DM us your order id — we'll track it in 5 min."</i>
        </div>
        <div className="space-y-2">
          {brand.boilerplate_lines.map((line, i) => (
            <div key={i} className="flex items-center gap-2 border border-[color:var(--sg-border)] rounded-md bg-white px-3 py-1.5" data-testid={`boilerplate-line-${i}`}>
              <span className="text-sm flex-1">{line}</span>
              <button
                type="button"
                data-testid={`boilerplate-remove-${i}`}
                className="text-[color:var(--sg-muted)] hover:text-rose-600"
                onClick={() => set("boilerplate_lines", brand.boilerplate_lines.filter((_, j) => j !== i))}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <Input
              data-testid="boilerplate-new"
              value={newBoiler}
              onChange={(e) => setNewBoiler(e.target.value)}
              placeholder="Add a line the AI can reuse verbatim…"
              className="bg-white flex-1"
              onKeyDown={(e) => {
                if (e.key === "Enter" && newBoiler.trim()) {
                  e.preventDefault();
                  set("boilerplate_lines", [...brand.boilerplate_lines, newBoiler.trim()]);
                  setNewBoiler("");
                }
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!newBoiler.trim()}
              onClick={() => {
                set("boilerplate_lines", [...brand.boilerplate_lines, newBoiler.trim()]);
                setNewBoiler("");
              }}
              data-testid="boilerplate-add"
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Add
            </Button>
          </div>
        </div>
      </div>

      {/* Save */}
      <div className="mt-6 pt-4 border-t border-[color:var(--sg-border)] flex items-center justify-between">
        <div className="text-[11px] text-[color:var(--sg-muted)]">
          {q.data?.updated_at ? `Last updated ${new Date(q.data.updated_at).toLocaleString()}` : "Not saved yet"}
        </div>
        <Button
          data-testid="brand-profile-save"
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white"
        >
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Save brand profile
        </Button>
      </div>
    </div>
  );
};
